import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SignedImage } from '../../hooks/useSignedUrl';
import { LoanOfficer, ProgressWithStep } from '../../types';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface HandsOnLO extends LoanOfficer {
  handsOnTotal: number;
  handsOnCompleted: number;
}

const HandsOnRoster: React.FC = () => {
  const navigate = useNavigate();
  const { isVA } = useAuth();
  const navigation = getAdminNavigation(isVA);
  const [loanOfficers, setLoanOfficers] = useState<HandsOnLO[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch all LO profiles
      const { data: losData, error: losError } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, email, headshot, user_role, created_at')
        .eq('user_role', 'user')
        .order('created_at', { ascending: false });

      if (losError) throw losError;

      // Fetch all progress with step data, filter to hands_on section client-side
      const { data: progressData, error: progressError } = await supabase
        .from('loan_officer_progress')
        .select('*, step:onboarding_steps!inner(*)')
        .eq('step.is_active', true);

      if (progressError) throw progressError;

      // Filter to hands_on steps and group by LO
      const handsOnProgress = (progressData || []).filter(
        (p: ProgressWithStep) => p.step?.section === 'hands_on'
      );

      const progressByLO = new Map<string, { total: number; completed: number }>();
      handsOnProgress.forEach((p: ProgressWithStep) => {
        const loId = String(p.loan_officer_id);
        if (!progressByLO.has(loId)) {
          progressByLO.set(loId, { total: 0, completed: 0 });
        }
        const entry = progressByLO.get(loId)!;
        entry.total++;
        if (p.status === 'completed') entry.completed++;
      });

      // Merge — only include LOs that have hands-on steps
      const merged: HandsOnLO[] = (losData || [])
        .map((lo) => {
          const id = String(lo.id);
          const stats = progressByLO.get(id);
          if (!stats || stats.total === 0) return null;
          return {
            ...lo,
            id,
            handsOnTotal: stats.total,
            handsOnCompleted: stats.completed,
          } as HandsOnLO;
        })
        .filter(Boolean) as HandsOnLO[];

      setLoanOfficers(merged);
    } catch {
      alert('Failed to load hands-on training data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    let debounceTimer: ReturnType<typeof setTimeout>;
    const channel = supabase
      .channel('hands-on-progress')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loan_officer_progress',
        },
        () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => loadData(), 500);
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return loanOfficers;
    const q = searchQuery.toLowerCase();
    return loanOfficers.filter((lo) => {
      const name = `${lo.first_name ?? ''} ${lo.last_name ?? ''}`.toLowerCase();
      return name.includes(q) || lo.email?.toLowerCase().includes(q);
    });
  }, [loanOfficers, searchQuery]);

  return (
    <AdminShell
      title="Hands On Training"
      description="Track in-person training progress for each loan officer."
      nav={navigation}
    >
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white text-sm text-slate-700 border border-slate-200 focus:outline-none focus:border-lumin-primary/40"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-500">
          <div className="h-12 w-12 border-2 border-slate-200 border-t-lumin-primary rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center">
          <p className="text-slate-500 text-sm">
            {searchQuery ? 'No loan officers match your search.' : 'No loan officers have hands-on training steps assigned yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Loan Officer</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Completed</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lo) => {
                const pct = lo.handsOnTotal > 0
                  ? Math.round((lo.handsOnCompleted / lo.handsOnTotal) * 100)
                  : 0;

                return (
                  <tr
                    key={lo.id}
                    onClick={() => navigate(`/admin/loan-officers/${lo.id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-slate-200 flex-shrink-0 overflow-hidden">
                          {lo.headshot ? (
                            <SignedImage
                              src={lo.headshot}
                              alt={`${lo.first_name} ${lo.last_name}`}
                              className="h-full w-full object-cover"
                              bucket="onboarding-documents"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-slate-500 text-xs font-semibold">
                              {(lo.first_name?.[0] || '') + (lo.last_name?.[0] || '')}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {lo.first_name} {lo.last_name}
                          </p>
                          <p className="text-xs text-slate-500">{lo.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-[200px] h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              pct === 100 ? 'bg-emerald-500' : 'bg-lumin-primary'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-600 font-medium w-10">{pct}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-700">
                        {lo.handsOnCompleted} / {lo.handsOnTotal}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
};

export default HandsOnRoster;
